import { useEffect, useRef, useState } from "react";
import { useFlowNavigation } from "@/components/flow/flow-navigation-context";
import type { GoalNavEntry } from "@/components/flow/goal-nav-types";
import type { FlowPage } from "@/lib/types/flow";

type UseGoalContextNavArgs = {
  /** The active goal. Changing this resets the within-goal selection. */
  goalId: string;
  /** Goal pages placed before the portfolio dimension (pageType === "goal"). */
  goalPagesBefore: FlowPage[];
  /** QL-117 — goal pages placed after the portfolio dimension (pageType === "goal"). */
  goalPagesAfter: FlowPage[];
  /** Portfolio pages (pageType === "portfolio"). */
  portfolioPages: FlowPage[];
  /** The active goal's portfolios (already fetched by the data wrapper). */
  portfolios: Array<{ id: string }>;
  /** True while the portfolios are still being fetched (the landing rule waits for them). */
  isLoadingPortfolios?: boolean;
  /** Whether this flow allows multiple portfolios per goal. */
  multiPortfolio: boolean;
  /** QP-172 — called when a pending portfolio navigation is consumed. */
  onPendingNavigated?: () => void;
};

export type GoalContextNav = {
  /** Which goal-context tab is active (goal page vs portfolio vs direct portfolio page). */
  activeEntry: GoalNavEntry | null;
  /** The selected portfolio instance (multiPortfolio only); null while loading/empty. */
  activePortfolioId: string | null;
  /** Which portfolio page is open within the selected portfolio. */
  activePortfolioPageIndex: number;
  /** Select a goal-context tab. Selecting a portfolio also sets the active portfolio + resets its page. */
  selectEntry: (entry: GoalNavEntry) => void;
  /** Move within the selected portfolio's page sub-nav. */
  setActivePortfolioPageIndex: (index: number) => void;
};

/**
 * The default tab a goal opens on (QL-117 landing rule): the first goal page before the
 * portfolios; if that group is empty, the first portfolio (multiPortfolio) or portfolio page; if
 * there is neither, the first goal page after the portfolios. While the portfolios are still
 * loading the rule waits rather than landing on an after-page that a portfolio would outrank.
 */
function computeDefaultEntry(
  goalPagesBefore: FlowPage[],
  portfolios: Array<{ id: string }>,
  portfolioPages: FlowPage[],
  multiPortfolio: boolean,
  goalPagesAfter: FlowPage[],
  isLoadingPortfolios: boolean,
): GoalNavEntry | null {
  if (goalPagesBefore.length > 0) {
    return { kind: "goalPage", pageId: goalPagesBefore[0].id };
  }
  if (multiPortfolio) {
    if (portfolios.length > 0) return { kind: "portfolio", portfolioId: portfolios[0].id };
    if (isLoadingPortfolios) return null;
  } else if (portfolioPages.length > 0) {
    return { kind: "portfolioPageDirect", pageId: portfolioPages[0].id };
  }
  if (goalPagesAfter.length > 0) {
    return { kind: "goalPage", pageId: goalPagesAfter[0].id };
  }
  return null;
}

/**
 * Per-goal navigation state for the advisor goal-context row (QP-150).
 *
 * Holds "what's selected right now" for one goal — the active tab (goal page or portfolio), the
 * active portfolio, and the portfolio-page sub-nav index — plus the guard rules that keep those
 * consistent: sensible default on open, reset on goal switch, and self-correction while portfolios
 * load or when the selected portfolio disappears. Pure selection state; no data fetching, no render.
 */
export function useGoalContextNav({
  goalId,
  goalPagesBefore,
  goalPagesAfter,
  portfolioPages,
  portfolios,
  isLoadingPortfolios = false,
  multiPortfolio,
  onPendingNavigated,
}: UseGoalContextNavArgs): GoalContextNav {
  const [activeEntry, setActiveEntry] = useState<GoalNavEntry | null>(null);
  const [activePortfolioId, setActivePortfolioId] = useState<string | null>(null);
  const [activePortfolioPageIndex, setActivePortfolioPageIndex] = useState(0);

  // Ref tracks the latest activePortfolioId synchronously so the guard effect can read it without
  // depending on the state value (which would cause the guard to fire on every selectEntry call,
  // racing with not-yet-refetched portfolio lists).
  const activePortfolioIdRef = useRef<string | null>(null);

  // Reset the within-goal selection whenever the active goal changes. The default effect below
  // re-seeds `activeEntry` once inputs are known; `activePortfolioId` is re-seeded by its own
  // effect when the new goal's portfolios arrive.
  useEffect(() => {
    setActiveEntry(null);
    setActivePortfolioPageIndex(0);
    activePortfolioIdRef.current = null;
  }, [goalId]);

  // QP-172 — pending portfolio navigation. Checked BEFORE the activeEntry guard
  // so it works both when switching to a new goal (activeEntry is null) and when
  // already on the goal (activeEntry is set to a goal page).
  //
  // Does NOT clear the pending inside this effect — React 18 Strict Mode
  // double-fires effects in dev, and clearing on the first fire causes the
  // second fire to fall through to the default. A separate cleanup effect
  // clears the pending only after the activeEntry state has committed to the
  // matching portfolio.
  const flowNav = useFlowNavigation();
  useEffect(() => {
    if (!flowNav) return;
    const pendingPortfolioId = flowNav.peekPendingPortfolio(goalId);
    if (!pendingPortfolioId) return;

    if (portfolios.some((p) => p.id === pendingPortfolioId)) {
      if (multiPortfolio) {
        // Multi-portfolio: select the portfolio instance tab.
        setActiveEntry({ kind: "portfolio", portfolioId: pendingPortfolioId });
      } else if (portfolioPages.length > 0) {
        // Single-portfolio: select the first portfolio page directly.
        setActiveEntry({ kind: "portfolioPageDirect", pageId: portfolioPages[0].id });
      }
      setActivePortfolioId(pendingPortfolioId);
      activePortfolioIdRef.current = pendingPortfolioId;
      setActivePortfolioPageIndex(0);
      onPendingNavigated?.();
    }
    // If portfolio not loaded yet, this effect re-fires when `portfolios` changes.
  }, [flowNav, goalId, portfolios, portfolioPages, multiPortfolio, onPendingNavigated]);

  // Clear the pending after the navigation has committed to state. Only clears
  // when the active portfolio matches the pending target, so a new
  // navigateToPortfolio call (which changes flowNav via tick) won't lose its
  // pending before the main effect above can consume it.
  useEffect(() => {
    if (!flowNav || !activeEntry) return;
    // Match portfolio tab (multi) or portfolio page direct (single).
    const navigatedPortfolioId =
      activeEntry.kind === "portfolio"
        ? activeEntry.portfolioId
        : activeEntry.kind === "portfolioPageDirect"
          ? activePortfolioIdRef.current
          : null;
    if (!navigatedPortfolioId) return;
    const pending = flowNav.peekPendingPortfolio(goalId);
    if (pending && pending === navigatedPortfolioId) {
      flowNav.clearPendingNavigation();
    }
  }, [activeEntry, flowNav, goalId]);

  // QL-120 — page visibility is a function of the goal (and portfolio), so a page that is open
  // can become hidden without the goal changing: filling in a risk assessment, switching a
  // portfolio's type, or picking a goal type all re-run the rules. Drop the stale selection so
  // the default effect below re-seeds it, instead of rendering an empty body under no active tab.
  useEffect(() => {
    if (!activeEntry) return;
    const stale =
      (activeEntry.kind === "goalPage" &&
        !goalPagesBefore.some((p) => p.id === activeEntry.pageId) &&
        !goalPagesAfter.some((p) => p.id === activeEntry.pageId)) ||
      (activeEntry.kind === "portfolioPageDirect" &&
        !portfolioPages.some((p) => p.id === activeEntry.pageId));
    if (stale) setActiveEntry(null);
  }, [activeEntry, goalPagesBefore, goalPagesAfter, portfolioPages]);

  // Ensure there's always a valid default entry once the goal pages / portfolios are known.
  // Skips when a pending navigation exists (the effect above will handle it).
  useEffect(() => {
    if (activeEntry === null) {
      if (flowNav?.peekPendingPortfolio(goalId)) return;
      const def = computeDefaultEntry(
        goalPagesBefore,
        portfolios,
        portfolioPages,
        multiPortfolio,
        goalPagesAfter,
        isLoadingPortfolios,
      );
      if (def) setActiveEntry(def);
    }
  }, [
    activeEntry,
    goalPagesBefore,
    goalPagesAfter,
    portfolios,
    portfolioPages,
    multiPortfolio,
    isLoadingPortfolios,
    flowNav,
    goalId,
  ]);

  // Keep the active portfolio valid: null while empty/loading, snap to the first when the current
  // selection is missing (e.g. a portfolio was deleted, or the goal just switched).
  // Only depends on `portfolios` (not `activePortfolioId`); reads the latest selection from the
  // ref so it won't fire when `selectEntry` sets a newly-created portfolio ID that hasn't appeared
  // in the (not-yet-refetched) portfolios list.
  // Note: this intentionally does NOT update `activeEntry` — the view layer (GoalContextView)
  // detects the divergence between activeEntry and activePortfolioId and corrects the tab value.
  useEffect(() => {
    if (portfolios.length === 0) {
      setActivePortfolioId(null);
      activePortfolioIdRef.current = null;
      return;
    }
    const currentId = activePortfolioIdRef.current;
    if (!currentId || !portfolios.some((p) => p.id === currentId)) {
      const fallbackId = portfolios[0].id;
      setActivePortfolioId(fallbackId);
      activePortfolioIdRef.current = fallbackId;
      setActivePortfolioPageIndex(0);
    }
  }, [portfolios]);

  const selectEntry = (entry: GoalNavEntry) => {
    setActiveEntry(entry);
    if (entry.kind === "portfolio") {
      setActivePortfolioId(entry.portfolioId);
      activePortfolioIdRef.current = entry.portfolioId;
      setActivePortfolioPageIndex(0);
    }
  };

  return {
    activeEntry,
    activePortfolioId,
    activePortfolioPageIndex,
    selectEntry,
    setActivePortfolioPageIndex,
  };
}
